import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import TopNav from "../components/TopNav";
import AppShell from "../components/AppShell";
import SEO from "../components/SEO";
import Footer from "../components/Footer";
import { api, useAuth } from "../context/AuthContext";
import { ArrowLeft, Loader2 } from "lucide-react";

// Minimal Markdown renderer: headings, bold, italics, links, lists, paragraphs.
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="my-4 pl-5 list-disc space-y-2 text-white/75">
          {listBuf.map((li, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inlineMd(li) }} />)}
        </ul>
      );
      listBuf = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushList(); continue; }
    if (line.startsWith("### ")) { flushList(); out.push(<h3 key={out.length} className="mt-8 mb-2 text-lg font-semibold text-white">{line.slice(4)}</h3>); continue; }
    if (line.startsWith("## ")) { flushList(); out.push(<h2 key={out.length} className="mt-10 mb-3 text-2xl font-bold tracking-tight text-white">{line.slice(3)}</h2>); continue; }
    if (line.startsWith("# ")) { flushList(); out.push(<h1 key={out.length} className="mt-8 mb-4 text-3xl font-black tracking-tight text-white">{line.slice(2)}</h1>); continue; }
    if (/^\s*[-*]\s+/.test(line)) { listBuf.push(line.replace(/^\s*[-*]\s+/, "")); continue; }
    flushList();
    out.push(<p key={out.length} className="my-4 text-white/75 leading-relaxed text-[15.5px]" dangerouslySetInnerHTML={{ __html: inlineMd(line) }} />);
  }
  flushList();
  return out;
}

function inlineMd(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, t, u) => `<a href="${u}" target="_blank" rel="noreferrer" class="text-white hover:text-white/70 underline decoration-white/20 underline-offset-2">${t}</a>`)
    .replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>")
    .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<i>$2</i>");
}

export default function NewsDetail() {
  const { slug } = useParams();
  const { user, loading } = useAuth();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/news/${slug}`).then((r) => setArticle(r.data.article)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    api.get("/news?limit=50").then((r) => {
      const mine = new Set([...(article.tags || "").split(","), ...(article.related_games || "").split(",")].map((s) => s.trim()).filter(Boolean));
      const pool = (r.data.articles || []).filter((a) => a.slug !== article.slug);
      const scored = pool.map((a) => {
        const theirs = new Set([...(a.tags || "").split(","), ...(a.related_games || "").split(",")].map((s) => s.trim()).filter(Boolean));
        let score = 0;
        mine.forEach((t) => { if (theirs.has(t)) score += 1; });
        return { a, score };
      }).sort((x, y) => y.score - x.score);
      setRelated(scored.slice(0, 3).map((s) => s.a));
    }).catch(() => {});
  }, [article]);

  const frame = (inner, nav = <TopNav variant="app" />) => user
    ? <AppShell>{inner}</AppShell>
    : <div className="relative min-h-screen text-white">{nav}{inner}</div>;

  if (loading && !article && !notFound) return <div className="min-h-screen flex items-center justify-center text-white/50"><Loader2 className="animate-spin" /></div>;

  if (notFound) {
    return frame(
      <div className={`${user ? "pt-16" : "pt-32"} px-6 text-center`}>
        <div className="text-white/60">Article not found.</div>
        <Link to="/news" className="mt-4 inline-block text-white/70 hover:text-white">Back to Journal</Link>
      </div>
    );
  }

  if (!article) return <div className="min-h-screen flex items-center justify-center text-white/50"><Loader2 className="animate-spin" /></div>;

  return frame(
    <>
      <SEO
        title={article.title}
        description={article.excerpt || `${article.title} — Gumble Journal`}
        path={`/news/${article.slug}`}
        image={article.cover}
        type="article"
      />
      <article className={`${user ? "pt-10 md:pt-14" : "pt-28"} px-6 md:px-10 max-w-3xl mx-auto pb-20`}>
        <Link to="/news" className="inline-flex items-center gap-1.5 text-[13px] text-white/50 hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Journal
        </Link>
        <div className="mt-6 text-[11px] uppercase tracking-widest text-white/40">
          {new Date(article.created_at).toLocaleDateString()} · {article.author || "Gumble Editorial"}
        </div>
        <h1 className="mt-3 text-4xl md:text-5xl font-black tracking-[-0.02em] leading-[1.05]">{article.title}</h1>
        {article.cover && (
          <div className="mt-8 aspect-[16/9] rounded-3xl overflow-hidden">
            <img src={article.cover} alt={`${article.title} — featured art`} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="mt-8 prose-invert">
          {renderMarkdown(article.body)}
        </div>
        <div className="mt-12 rounded-3xl border border-white/10 bg-white/[0.03] p-6 text-center">
          <div className="text-[11px] uppercase tracking-widest text-white/40">Play Gumble on Stake</div>
          <a href="https://stake.com/fr/casino/group/gumble?c=gumble" target="_blank" rel="noreferrer sponsored" className="mt-3 inline-block">
            <img src="/Stake_Casino_320x50.gif" alt="Play on Stake" width={320} height={50} className="mx-auto rounded-md" loading="lazy" />
          </a>
        </div>
        <ShareRow title={article.title} copied={copied} setCopied={setCopied} />
        {related.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-bold tracking-tight">Related reading</h2>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map((a) => (
                <Link key={a.id} to={`/news/${a.slug}`} className="group glass rounded-2xl overflow-hidden">
                  {a.cover && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={a.cover} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-4 text-[14px] font-semibold leading-snug">{a.title}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
      <Footer />
    </>
  );
}

function ShareRow({ title, copied, setCopied }) {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const shareX = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title + " — Gumble Journal")}&url=${encodeURIComponent(url)}`;
  const copy = () => {
    try {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  return (
    <div className="mt-8 flex items-center gap-2">
      <span className="text-[11px] uppercase tracking-widest text-white/40 mr-1">Share</span>
      <a href={shareX} target="_blank" rel="noreferrer" className="rounded-full glass glass-hover px-4 py-2 text-[12.5px] text-white/80">Post on X</a>
      <button onClick={copy} className="rounded-full glass glass-hover px-4 py-2 text-[12.5px] text-white/80">
        {copied ? "Link copied" : "Copy link"}
      </button>
    </div>
  );
}
